import { axiosInstance } from "@/config/axios-wrapper";
import { V2Response } from "@/types/api";
import { Job } from "@/types/job";
import { Meeting } from "@/types/meeting";
import { FavouritedJobseeker, Organization, Teammate } from "@/types/organization";

export const getOrganization = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<Organization>>(
    `/organizations/${organizationId}`,
  );
  return response.data.result;
};

export const updateOrganization = async (organizationId: string, data) => {
  const response = await axiosInstance.put<V2Response<any>>(
    `organizations/${organizationId}`,
    data,
  );
  return response.data.result;
};

export const updateOrganizationLogo = async (organizationId: string, data: FormData) => {
  const response = await axiosInstance.post<V2Response<{ logo: string }>>(
    `organizations/${organizationId}/logo`,
    data,
  );
  return response.data.result;
};

export const updateOrganizationVideo = async (organizationId: string, data: FormData) => {
  const response = await axiosInstance.post<V2Response<{ video: string }>>(
    `organizations/${organizationId}/video`,
    data,
  );
  return response.data.result;
};

export const deleteOrganizationVideo = async (organizationId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `organizations/${organizationId}/video`,
  );
  return response.data.result;
};

export const getOrganizationNotifications = async (
  data: { organizationId: string },
  options: { cursor?: string },
) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `${`/organizations/${data.organizationId}`}/notifications${
      options.cursor ? `?cursor=${options.cursor}` : ""
    }`,
  );

  return response.data.result;
};

export const createOrganizationFavourite = async ({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${organizationId}/favourites/${userId}`,
  );
  return response.data.result;
};

export const getOrganizationFavourites = async (
  organizationId: string,
  options?: { q?: string; sort?: string },
) => {
  const response = await axiosInstance.get<
    V2Response<{ favourites: FavouritedJobseeker[]; total: number }>
  >(`/organizations/${organizationId}/favourites`, {
    params: {
      q: options?.q,
      sort: options?.sort,
    },
  });
  return response.data.result;
};

export const deleteOrganizationFavourite = async ({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/organizations/${organizationId}/favourites/${userId}`,
  );
  return response.data.result;
};

export const createOrganizationMeeting = async (
  organizationId: string,
  data: {
    application_id: string;
    timezone: string;
    start_time: string;
    duration: number;
    mode: string;
    address?: {
      line1: string;
      line2: string;
      country: string;
      state: string;
      city: string;
      zip_code: string;
      details: string;
    };
    details: string;
  },
) => {
  const response = await axiosInstance.post<V2Response<Meeting>>(
    `/organizations/${organizationId}/meetings`,
    data,
  );
  return response.data.result;
};

export const uploadOrganizationMeetingFile = async (
  {
    organizationId,
    meetingId,
  }: {
    organizationId: string;
    meetingId: string;
  },
  formData: FormData,
) => {
  const response = await axiosInstance.post<V2Response<{ file: string }>>(
    `/organizations/${organizationId}/meetings/${meetingId}/file`,
    formData,
  );
  return response.data.result;
};

export const getOrganizationMeetings = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<{ meetings: Meeting[] }>>(
    `/organizations/${organizationId}/meetings`,
  );
  return response.data.result;
};

export const getOrganizationMeeting = async ({
  organizationId,
  meetingId,
}: {
  organizationId: string;
  meetingId: string;
}) => {
  const response = await axiosInstance.get<V2Response<{ meetings: Meeting[] }>>(
    `/organizations/${organizationId}/meetings`,
  );
  return response.data.result.meetings?.find((meeting) => meeting.id === meetingId);
};

export const updateOrganizationMeeting = async (
  data: { organizationId: string; meetingId: string },
  updatedData: any,
) => {
  const response = await axiosInstance.put<V2Response<any>>(
    `/organizations/${data.organizationId}/meetings/${data.meetingId}`,
    updatedData,
  );
  return response.data.result;
};

export const deleteOrganizationMeeting = async ({
  organizationId,
  meetingId,
}: {
  organizationId: string;
  meetingId: string;
}) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/organizations/${organizationId}/meetings/${meetingId}`,
  );
  return response.data.result;
};

export const createOrganizationCheckoutSession = async (
  id: string,
  { product_id }: { product_id: string },
) => {
  const response = await axiosInstance.post<
    V2Response<
      { url: string } | { subscriptionId: string; clientSecret: string | null; status: string }
    >
  >(`/organizations/${id}/create-checkout-session`, { product_id });
  return response.data.result;
};

export const cancelOrganizationSubscription = async (organizationId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `organizations/${organizationId}/subscription/cancel`,
  );
  return response.data.result;
};

export const activateOrganizationSubscriptionRenewal = async (organizationId: string) => {
  const response = await axiosInstance.post<V2Response<{ url: string }>>(
    `/organizations/${organizationId}/subscription/activate-renewal`,
  );
  return response.data.result;
};

export const createOrganizationBillingPortalSession = async (
  organizationId: string,
  data?: { return_url?: string },
) => {
  const response = await axiosInstance.post<V2Response<{ url: string }>>(
    `/organizations/${organizationId}/billing-portal-session`,
    data,
  );
  return response.data.result;
};

export const getTeammates = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<{ teammates: Teammate[] }>>(
    `/organizations/${organizationId}/teammates`,
  );
  return response.data.result;
};

export const createTeammate = async (
  organizationId: string,
  teammate: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    role: string;
    permissions: string[];
    permitted_locations: { state: string; city: string; zip_code: string }[];
    permitted_all_locations: boolean;
  },
) => {
  const response = await axiosInstance.post<V2Response<Teammate>>(
    `/organizations/${organizationId}/teammates`,
    teammate,
  );
  return response.data.result;
};

export const updateTeammate = async (
  { organizationId, teammateId }: { organizationId: string; teammateId: string },
  teammate: {
    first_name?: string;
    last_name?: string;
    email?: string;
    role?: string;
    permissions?: string[];
    permitted_locations?: { state: string; city: string; zip_code: string }[];
    permitted_all_locations: boolean;
  },
) => {
  const response = await axiosInstance.put<V2Response<Teammate>>(
    `/organizations/${organizationId}/teammates/${teammateId}`,
    teammate,
  );
  return response.data.result;
};

export const deleteTeammate = async ({
  organizationId,
  teammateId,
}: {
  organizationId: string;
  teammateId: string;
}) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/organizations/${organizationId}/teammates/${teammateId}`,
  );
  return response.data.result;
};

export const getOrganizationJobs = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<{ jobs: Job[] }>>(
    `/organizations/${organizationId}/jobs`,
  );
  return response.data.result;
};
